import React, { useMemo } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import WfbAdsComponent from '@fuww/library/src/WfbAds';
import AdaptiveCard from '@fuww/library/src/Cards/AdaptiveCard';
import breakpoints from '@fuww/library/src/Theme/breakpoints';
import { useSiteContext } from '../SiteContext';
import withQuery from '../../lib/withQuery';
import wfbProfilesQuery from '../../lib/queries/jobs/wfbProfilesQuery.graphql';
import useSeed from '../../lib/useSeed';
import seededSampleSize from '../../lib/seededSampleSize';
import {
  companyProfilesCardImageConfigurations,
  wfbAdsLogoImageConfigurations,
} from '../../lib/imageConfigurations';
import ImageWithUrls from '../ImageWithUrls';
import LinkAndAnchor from '../LinkAndAnchor';
import getProxiedImages from '../../lib/getProxiedImages';
import profilesLimit from '../../lib/profilesLimit';

const WFB_LIMIT = 20;

const wfbAdsPropertyTypes = {
  data: PropTypes.shape({
    profiles: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        page: PropTypes.shape({
          slug: PropTypes.string,
          imageUrls: PropTypes.arrayOf(PropTypes.string),
          mobileImageUrls: PropTypes.arrayOf(PropTypes.string),
        }),
        company: PropTypes.shape({
          fullName: PropTypes.string,
          logoUrls: PropTypes.arrayOf(
            PropTypes.string,
          ),
        }).isRequired,
      }),
    ).isRequired,
  }).isRequired,
  variables: PropTypes.shape({}),
  limit: PropTypes.number,
  preloadFirst: PropTypes.bool,
};

type WfbAdsProperties = InferProps<typeof wfbAdsPropertyTypes> & {
  limit?: number;
};

export const WfbAdsWithoutQuery = ({
  data: { profiles }, variables, limit, preloadFirst,
}: WfbAdsProperties) => {
  const { locale } = useSiteContext();
  const seed = useSeed();

  const wfbAds = useMemo(() => {
    const selectedProfiles = seededSampleSize(
      [...profiles], limit ?? WFB_LIMIT, seed,
    );

    return selectedProfiles.map(({
      id,
      page: {
        slug,
        imageUrls: desktopImageUrls,
        mobileImageUrls,
      },
      company: { fullName, logoUrls },
    }, index) => {
      const originalImageUrls = mobileImageUrls[0]
        ? mobileImageUrls
        : desktopImageUrls;

      const { imageUrls, srcSet } = getProxiedImages(
        variables,
        originalImageUrls,
      );

      const fetchPriority = (preloadFirst && index === 0) ? 'high' : 'low';

      return (
        <LinkAndAnchor
          key={id}
          route={`jobs.${locale}`}
          params={{ companySlug: slug }}
          useRouter
        >
          <AdaptiveCard
            title={fullName}
            image={imageUrls[0] && (
              <ImageWithUrls
                sizes={`
                  (min-width: 1440px) 318px,
                  (min-width: ${breakpoints.lg}px) calc(25vw - 42px),
                  (min-width: ${breakpoints.md}px) calc(50vw - 52px),
                  calc(100vw - 72px)
                `}
                srcSet={srcSet}
                imageUrls={imageUrls}
                alt={fullName}
                disableRoundedCorners
                preload={preloadFirst && index === 0}
                finalFetchpriority={fetchPriority}
                placeholderFetchpriority={fetchPriority}
              />
            )}
            logo={logoUrls[0] && (
              <ImageWithUrls
                sizes="168px"
                imageUrls={logoUrls}
                alt={fullName}
                srcSet={`
                  ${logoUrls[1]} 168w, 
                  ${logoUrls[2]} 336w`}
                objectFit="contain"
                padding="0 1rem"
              />
            )}
          />
        </LinkAndAnchor>
      );
    });
  }, [profiles, seed, locale, variables, limit, preloadFirst]);

  return wfbAds.length > 0 ? (
    <WfbAdsComponent wfbAds={wfbAds} />
  ) : null;
};

WfbAdsWithoutQuery.propTypes = wfbAdsPropertyTypes;

const WfbAdsWithQuery = withQuery(WfbAdsWithoutQuery);

const WfbAds = ({ limit }: { limit?: number }) => {
  const { jobsLocale } = useSiteContext();

  return (
    <WfbAdsWithQuery
      query={wfbProfilesQuery}
      variables={{
        limit: profilesLimit,
        locales: [jobsLocale],
        imageConfigurations: companyProfilesCardImageConfigurations,
        logoConfigurations: wfbAdsLogoImageConfigurations,
      }}
      errorMessage=""
      limit={limit}
      preloadFirst
    />
  );
};

export default WfbAds;
